import type {
    BackendModule,
    MultiReadCallback,
    ReadCallback,
    Services,
} from 'i18next';
import {
    ContentfulClientApi,
    CreateClientParams,
    createClient,
} from 'contentful';

import type { TranslationSkeleton } from './types';

const DEFAULT_CONTENTFUL_SPACE = process.env.CONTENTFUL_SPACE!;
const DEFAULT_CONTENTFUL_ENVIRONMENT = process.env.CONTENTFUL_ENVIRONMENT!;
const DEFAULT_CONTENTFUL_ACCESS_TOKEN = process.env.CONTENTFUL_ACCESS_TOKEN!;

export class ContentfulBackend implements BackendModule {
    private client: ContentfulClientApi<undefined>;
    public type: 'backend';
    // https://github.com/i18next/i18next/issues/1440
    public static type = 'backend' as const;

    public init(
        _services: Services | null,
        options: Partial<CreateClientParams> = {}
    ): void {
        this.client = createClient({
            space: DEFAULT_CONTENTFUL_SPACE,
            environment: DEFAULT_CONTENTFUL_ENVIRONMENT,
            accessToken: DEFAULT_CONTENTFUL_ACCESS_TOKEN,
            ...options,
        });
    }

    public read(
        language: string,
        namespace: string,
        callback: ReadCallback
    ): void {
        this.getTranslations({
            locale: language,
            'fields.namespace[in]': namespace,
            content_type: 'translation',
            limit: 1000,
        })
            .then((items) =>
                items.reduce(
                    (acc, entry) => ({
                        ...acc,
                        [entry.fields.key]: entry.fields.text,
                    }),
                    {}
                )
            )
            .then((items) => callback(null, items))
            .catch((e) => callback(e, null));
    }

    public readMulti(
        languages: string[],
        namespaces: string[],
        callback: MultiReadCallback
    ): void {
        // get namespaces for all languages
        // contentful doesn't allow to use multiple locales in one query
        Promise.all(
            languages.map(
                async (lang) =>
                    await this.getTranslations({
                        locale: lang,
                        'fields.namespace[in]': namespaces.join(','),
                        content_type: 'translation',
                        limit: 1000,
                    })
            )
        )
            .then((d) => {
                const translations = d.reduce((translations, items) => {
                    const translation = items.reduce((translation, item) => {
                        if (item.sys.locale) {
                            if (!(translation as any)[item.sys.locale]) {
                                (translation as any)[item.sys.locale] = {};
                            }
                            (translation as any)[item.sys.locale][item.sys.id] =
                                item.fields;
                        }
                        return translation;
                    }, {});

                    return {
                        ...translations,
                        ...translation,
                    };
                }, {});
                callback(null, translations);
            })
            .catch((e) => callback(e, null));
    }

    private async getTranslations(query: any) {
        const items = [];
        let skip = 0,
            total = 0;

        do {
            const entries = await this.client.getEntries<TranslationSkeleton>({
                skip,
                ...query,
            });

            items.push(...entries.items);

            total = entries.total;
            skip = items.length;
        } while (skip < total);

        return items;
    }
}
